import { apiClient } from './client';
import type {
  CitationAnalytics,
  CitationProbe,
  CitationRecommendation,
  AdTestTag,
  ContentEntry,
  LinkedInEngagementEntry,
  SequenceConversionEntry,
} from '@sequesto-os/shared';

// ─────────────────────────────────────────────────────────────────────────────
// Citation Analytics
// ─────────────────────────────────────────────────────────────────────────────

export async function getCitationAnalytics(startDate?: string, endDate?: string) {
  return apiClient.get<{ analytics: CitationAnalytics }>('/measurement/citations', {
    params: { startDate, endDate },
  });
}

export async function getCitationProbes() {
  return apiClient.get<{ probes: CitationProbe[] }>('/measurement/citations/probes');
}

export async function addCitationProbe(data: { query: string; category: string }) {
  return apiClient.post<{ probe: CitationProbe }>('/measurement/citations/probes', data);
}

export async function deleteCitationProbe(id: string) {
  return apiClient.delete(`/measurement/citations/probes/${id}`);
}

export interface ProbeHistoryResult {
  id: string;
  probeId: string;
  llmProvider: string;
  llmModel: string;
  responseText: string;
  cited: boolean;
  citationContext: string | null;
  listRank: number | null;
  citedUrls: Array<{ url: string; title?: string; isSequesto: boolean }>;
  grounded: boolean;
  /** Derived from visibility_mentions JOIN — replaces the legacy competitor_mentions JSONB column. */
  competitorNames: string[];
  runDate: string;
  createdAt: string;
}

export async function getProbeHistory(probeId: string) {
  return apiClient.get<{ results: ProbeHistoryResult[] }>(`/measurement/citations/probes/${probeId}/history`);
}

export interface TriggerSweepResponse {
  runId?: string;
  status: 'queued' | 'already_running';
}

export async function triggerCitationSweep() {
  return apiClient.post<TriggerSweepResponse>('/measurement/citations/run');
}

export type CitationRunStatus =
  | 'running'
  | 'completed'
  | 'completed_with_errors'
  | 'failed';

export interface CitationRun {
  id: string;
  runDate: string;
  status: CitationRunStatus;
  total: number;
  cited: number;
  errors: number;
  probeCount: number;
  clientCount: number;
  costUsd: number;
  budgetTripped: boolean;
  providersUsed: string[];
  errorsByProvider: Record<string, number>;
  errorMessage: string | null;
  alertPosted: boolean;
  startedAt: string;
  finishedAt: string | null;
  expected: number;
  done: number;
  percent: number;
}

export async function getLatestCitationRun(id?: string) {
  return id
    ? apiClient.get<{ run: CitationRun | null }>('/measurement/citations/runs/latest', { params: { id } })
    : apiClient.get<{ run: CitationRun | null }>('/measurement/citations/runs/latest');
}

// ─────────────────────────────────────────────────────────────────────────────
// Citation Recommendations
// ─────────────────────────────────────────────────────────────────────────────

export async function getCitationRecommendations(status: 'open' | 'done' | 'dismissed' | 'all' = 'open') {
  return apiClient.get<{ recommendations: CitationRecommendation[] }>(
    '/measurement/recommendations',
    { params: { status } },
  );
}

export async function updateCitationRecommendationStatus(
  id: string,
  status: 'open' | 'done' | 'dismissed',
) {
  return apiClient.post<{ recommendation: CitationRecommendation }>(
    `/measurement/recommendations/${id}/status`,
    { status },
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Ad Test Tags
// ─────────────────────────────────────────────────────────────────────────────

export async function getAdTestTags() {
  return apiClient.get<{ tags: AdTestTag[] }>('/measurement/ad-tests/tags');
}

export async function addAdTestTag(data: {
  platform: string;
  campaignId: string;
  campaignName?: string;
  adGroupId?: string;
  adGroupName?: string;
  messagingVariant: string;
  icpSegment: string;
}) {
  return apiClient.post<{ tag: AdTestTag }>('/measurement/ad-tests/tags', data);
}

export async function deleteAdTestTag(id: string) {
  return apiClient.delete(`/measurement/ad-tests/tags/${id}`);
}

// ─────────────────────────────────────────────────────────────────────────────
// Content Tracking
// ─────────────────────────────────────────────────────────────────────────────

export async function getContent() {
  return apiClient.get<{ entries: ContentEntry[] }>('/measurement/content');
}

export async function addContent(data: {
  url: string;
  title: string;
  contentType: string;
  author?: string;
  publishDate: string;
  wordCount?: number;
}) {
  return apiClient.post<{ entry: ContentEntry }>('/measurement/content', data);
}

// ─────────────────────────────────────────────────────────────────────────────
// LinkedIn Engagement
// ─────────────────────────────────────────────────────────────────────────────

export async function getLinkedInEngagement(startDate?: string, endDate?: string) {
  return apiClient.get<{ entries: LinkedInEngagementEntry[] }>('/measurement/linkedin', {
    params: { startDate, endDate },
  });
}

export async function addLinkedInEngagement(data: LinkedInEngagementEntry) {
  return apiClient.post<{ entry: LinkedInEngagementEntry }>('/measurement/linkedin', data);
}

// ─────────────────────────────────────────────────────────────────────────────
// Sequences
// ─────────────────────────────────────────────────────────────────────────────

export async function getSequences() {
  return apiClient.get<{ entries: SequenceConversionEntry[] }>('/measurement/sequences');
}
